/**
 * CKEditor Lazy Initialization Manager
 *
 * Provides lazy loading of CKEditor instances to improve page load performance.
 * Instead of initializing all editors on page load, this manager:
 * - Initializes editors in the active tab on page load
 * - Initializes editors in other tabs when the tab is clicked
 * - Uses a queue system to prevent blocking the main thread
 * - Handles HTMX partial page loads
 */

import { initializeInlineStylePriorityGuards } from "./InlineStylePriorityGuard";

// CKEditor types
interface EditorInstance {
  getData: () => string;
  setData: (data: string) => void;
  destroy: () => Promise<void>;
  model: {
    document: {
      on: (event: string, callback: () => void) => void;
    };
  };
}

interface PendingEditor {
  element: HTMLTextAreaElement;
  required: boolean;
  initialized: boolean;
}

interface CKEditorConfig {
  toolbar?: {
    items: string[];
  };
  image?: {
    toolbar: string[];
  };
  table?: {
    contentToolbar: string[];
  };
  simpleUpload?: {
    uploadUrl: string | null;
    headers: Record<string, string>;
  };
  fileUploader?: {
    fileTypes: string[];
  };
  licenseKey?: string;
}

declare const ClassicEditor: {
  create: (element: HTMLElement, config?: CKEditorConfig) => Promise<EditorInstance>;
};

/**
 * Singleton manager for lazy CKEditor initialization.
 */
export class CKEditorManager {
  private static instance: CKEditorManager | null = null;
  private pendingEditors: Map<string, PendingEditor> = new Map();
  private initializedEditors: Map<string, EditorInstance> = new Map();
  private initQueue: string[] = [];
  private isProcessingQueue = false;
  private formSubmitHandlers: WeakSet<HTMLFormElement> = new WeakSet();
  private observer: IntersectionObserver | null = null;
  private classicEditorReady: Promise<boolean> | null = null;

  private constructor() {
    this.setupIntersectionObserver();
    this.setupTabListeners();
  }

  /**
   * Gets the singleton instance of CKEditorManager.
   */
  public static getInstance(): CKEditorManager {
    if (!CKEditorManager.instance) {
      CKEditorManager.instance = new CKEditorManager();
    }
    return CKEditorManager.instance;
  }

  /**
   * Registers a textarea for lazy CKEditor initialization.
   */
  public register(element: HTMLTextAreaElement, required = false): void {
    const id = element.id;
    if (!id || this.pendingEditors.has(id)) {
      return;
    }

    this.pendingEditors.set(id, {
      element,
      required,
      initialized: false,
    });

    // Mark as registered
    element.setAttribute("data-ckeditor-registered", "true");

    // Create placeholder wrapper
    this.createPlaceholder(element);

    const wrapper = element.closest(".ckeditor-wrapper") as HTMLElement | null;
    // Check if editor is in a visible tab panel or not in a tab at all
    const tabPanel = element.closest('[role="tabpanel"]');
    const isInVisibleTab = !tabPanel?.classList.contains("hidden");

    if (isInVisibleTab) {
      // Observe for visibility (editor in active tab or not in tabs)
      if (wrapper && this.observer && this.isWrapperVisible(wrapper)) {
        this.observer.observe(wrapper);
      }
    }

    // Add click/focus listener as fallback
    if (wrapper) {
      const initHandler = (): void => {
        this.queueInitialization(id);
      };
      wrapper.addEventListener("click", initHandler, { once: true });
      wrapper.addEventListener("focus", initHandler, {
        once: true,
        capture: true,
      });
    }
  }

  /**
   * Creates a placeholder element that shows before CKEditor initializes.
   */
  private createPlaceholder(element: HTMLTextAreaElement): void {
    // Check if already wrapped
    if (element.closest(".ckeditor-wrapper")) {
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.className = "ckeditor-wrapper";
    wrapper.setAttribute("data-editor-id", element.id);

    // Create visible placeholder
    const placeholder = document.createElement("div");
    placeholder.className = "ckeditor-placeholder";
    placeholder.setAttribute("data-placeholder", "Click to edit...");
    placeholder.tabIndex = 0;

    // Wrap the textarea
    element.parentNode?.insertBefore(wrapper, element);
    wrapper.appendChild(placeholder);
    wrapper.appendChild(element);
  }

  /**
   * Sets up IntersectionObserver for visibility-based initialization.
   */
  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const wrapper = entry.target as HTMLElement;
            const editorId = wrapper.getAttribute("data-editor-id");
            if (editorId) {
              this.queueInitialization(editorId);
              this.observer?.unobserve(wrapper);
            }
          }
        }
      },
      {
        root: null,
        rootMargin: "100px", // Initialize slightly before coming into view
        threshold: 0,
      },
    );
  }

  /**
   * Sets up listeners for Flowbite tab clicks.
   */
  private setupTabListeners(): void {
    // Listen for tab button clicks
    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      const tabButton = target.closest("[data-tabs-target]");

      if (tabButton) {
        const targetSelector = tabButton.getAttribute("data-tabs-target");
        if (targetSelector) {
          // Small delay to let Flowbite show the panel first
          setTimeout(() => {
            this.initializeEditorsInPanel(targetSelector);
          }, 50);
        }
      }
    });
  }

  /**
   * Initializes all editors within a tab panel.
   */
  private initializeEditorsInPanel(panelSelector: string): void {
    const panel = document.querySelector(panelSelector);
    if (!panel) return;

    this.observeEditorsInContainer(panel);
  }

  /**
   * Queues an editor for initialization to prevent overwhelming the browser.
   */
  private queueInitialization(editorId: string): void {
    if (!this.initQueue.includes(editorId)) {
      this.initQueue.push(editorId);
      this.processQueue();
    }
  }

  /**
   * Processes the initialization queue, initializing one editor at a time.
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.initQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.initQueue.length > 0) {
      const editorId = this.initQueue.shift();
      if (editorId) {
        await this.initializeEditor(editorId);
        // Small delay between initializations to prevent blocking
        await this.delay(50);
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Initializes a single CKEditor instance.
   */
  private async initializeEditor(editorId: string): Promise<void> {
    const pending = this.pendingEditors.get(editorId);
    if (!pending || pending.initialized) {
      return;
    }

    const { element, required } = pending;
    const wrapper = element.closest(".ckeditor-wrapper");
    const placeholder = wrapper?.querySelector(".ckeditor-placeholder");

    const editorReady = await this.waitForClassicEditor();
    if (!editorReady) {
      console.warn("CKEditorManager: ClassicEditor not loaded, skipping initialization");
      return;
    }

    try {
      // Show loading state
      if (placeholder) {
        placeholder.classList.add("ckeditor-loading");
      }

      // Show the textarea (CKEditor will replace it)
      element.style.display = "";

      // Remove placeholder
      if (placeholder) {
        placeholder.remove();
      }

      // Mark wrapper as initialized
      if (wrapper) {
        wrapper.classList.add("ckeditor-initialized");
      }

      const editorConfig = this.getEditorConfig(element);
      const editor = await ClassicEditor.create(element, editorConfig);
      initializeInlineStylePriorityGuards(wrapper ?? document);

      // Store instance reference on element
      (element as HTMLTextAreaElement & { ckeditorInstance: EditorInstance }).ckeditorInstance =
        editor;
      this.initializedEditors.set(editorId, editor);
      pending.initialized = true;

      // Sync data on change
      editor.model.document.on("change:data", () => {
        element.value = editor.getData();
      });

      // Setup form validation for required fields
      if (required) {
        this.setupFormValidation(element);
      }
    } catch (error) {
      console.error(`Failed to initialize CKEditor for ${editorId}:`, error);
      // Restore textarea visibility on failure
      element.style.display = "";
      if (placeholder) {
        placeholder.remove();
      }
    }
  }

  /**
   * Sets up form validation for required CKEditor fields.
   * Uses a single handler per form to avoid duplicates.
   */
  private setupFormValidation(element: HTMLTextAreaElement): void {
    const form = element.closest("form");
    if (!form || this.formSubmitHandlers.has(form)) {
      return;
    }

    this.formSubmitHandlers.add(form);

    form.addEventListener("submit", (event) => {
      // Check all required editors in this form
      const requiredEditors = form.querySelectorAll<HTMLTextAreaElement>(
        'textarea[data-ckeditor-required="true"]',
      );

      for (const textarea of requiredEditors) {
        const editorInstance = (
          textarea as HTMLTextAreaElement & { ckeditorInstance?: EditorInstance }
        ).ckeditorInstance;
        if (editorInstance && editorInstance.getData().trim() === "") {
          alert("Content field is required.");
          event.preventDefault();
          return;
        }
      }
    });
  }

  /**
   * Force initializes all pending editors sequentially (useful before form submission).
   * Uses the queue system to prevent overwhelming the browser.
   */
  public async initializeAll(): Promise<void> {
    // Queue all uninitialized editors
    for (const [editorId, pending] of this.pendingEditors) {
      if (!pending.initialized && !this.initQueue.includes(editorId)) {
        this.initQueue.push(editorId);
      }
    }

    // Wait for queue to finish processing
    // If queue is already processing, wait for it to complete
    while (this.initQueue.length > 0 || this.isProcessingQueue) {
      if (!this.isProcessingQueue) {
        await this.processQueue();
      } else {
        await this.delay(50);
      }
    }
  }

  /**
   * Destroys all editor instances (for cleanup).
   */
  public async destroyAll(): Promise<void> {
    for (const [editorId, editor] of this.initializedEditors) {
      try {
        await editor.destroy();
      } catch (error) {
        console.error(`Failed to destroy CKEditor ${editorId}:`, error);
      }
    }
    this.initializedEditors.clear();
    this.pendingEditors.clear();
  }

  /**
   * Scans the DOM for uninitialized CKEditor textareas and registers them.
   */
  public reinitialize(): void {
    const textareas = document.querySelectorAll<HTMLTextAreaElement>(
      'textarea[data-ckeditor="true"]:not([data-ckeditor-registered])',
    );

    for (const textarea of textareas) {
      const required = textarea.getAttribute("data-ckeditor-required") === "true";
      this.register(textarea, required);
    }
  }

  /**
   * Observes editors within a container for lazy initialization.
   */
  public observeEditorsInContainer(container: Element): void {
    if (!this.observer) {
      return;
    }

    const wrappers = container.querySelectorAll<HTMLElement>(".ckeditor-wrapper");
    for (const wrapper of wrappers) {
      const editorId = wrapper.getAttribute("data-editor-id");
      if (!editorId) {
        continue;
      }

      const pending = this.pendingEditors.get(editorId);
      if (pending && !pending.initialized && this.isWrapperVisible(wrapper)) {
        this.observer.observe(wrapper);
      }
    }
  }

  /**
   * Gets the CKEditor instance for a textarea by ID.
   */
  public getEditor(editorId: string): EditorInstance | undefined {
    return this.initializedEditors.get(editorId);
  }

  /**
   * Checks if an editor has been initialized.
   */
  public isInitialized(editorId: string): boolean {
    const pending = this.pendingEditors.get(editorId);
    return pending?.initialized ?? false;
  }

  /**
   * Checks if there are any pending (uninitialized) editors.
   * Used to determine if form submission should be intercepted.
   */
  public hasPendingEditors(): boolean {
    for (const [, pending] of this.pendingEditors) {
      if (!pending.initialized) {
        return true;
      }
    }
    return false;
  }

  private isWrapperVisible(wrapper: HTMLElement): boolean {
    if (wrapper.closest(".hidden")) {
      return false;
    }
    return wrapper.getClientRects().length > 0;
  }

  private getEditorConfig(element: HTMLTextAreaElement): CKEditorConfig {
    if (element.classList.contains("django_ckeditor_5")) {
      const djangoConfig = this.getDjangoEditorConfig(element);
      if (djangoConfig) {
        return djangoConfig;
      }
    }

    // Minimal fallback config — all editors should use django-ckeditor-5 config
    return {
      licenseKey: window.CKEDITOR_LICENSE_KEY ?? "GPL",
    };
  }

  private getDjangoEditorConfig(element: HTMLTextAreaElement): CKEditorConfig | null {
    const scriptId = `${element.id}_script`;
    const uploadMeta = document.getElementById(`${scriptId}-ck-editor-5-upload-url`);
    const configSpan = document.getElementById(`${scriptId}-span`);

    if (!uploadMeta || !configSpan) {
      return null;
    }

    const uploadUrl = uploadMeta.getAttribute("data-upload-url");
    const uploadFileTypesRaw = uploadMeta.getAttribute("data-upload-file-types") || "[]";
    const csrfCookieName = uploadMeta.getAttribute("data-csrf_cookie_name") || "csrftoken";

    let config: CKEditorConfig = {};
    try {
      config = JSON.parse(configSpan.textContent ?? "{}", (_key, value) => {
        const match = value?.toString().match(/^\/(.*?)\/([gimy]*)$/);
        if (match) {
          return new RegExp(match[1], match[2]);
        }
        return value;
      }) as CKEditorConfig;
    } catch (error) {
      console.warn("CKEditorManager: Failed to parse CKEditor config JSON.", error);
    }

    config.simpleUpload = {
      uploadUrl,
      headers: {
        "X-CSRFToken": this.getCookie(csrfCookieName),
      },
    };
    config.fileUploader = {
      fileTypes: JSON.parse(uploadFileTypesRaw) as string[],
    };
    config.licenseKey = config.licenseKey ?? window.CKEDITOR_LICENSE_KEY ?? "GPL";

    return config;
  }

  private getCookie(name: string): string {
    let cookieValue = "";
    if (document.cookie && document.cookie !== "") {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const trimmed = cookie.trim();
        if (trimmed.substring(0, name.length + 1) === `${name}=`) {
          cookieValue = decodeURIComponent(trimmed.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  private waitForClassicEditor(timeoutMs = 5000): Promise<boolean> {
    if (typeof ClassicEditor !== "undefined") {
      return Promise.resolve(true);
    }

    if (this.classicEditorReady) {
      return this.classicEditorReady;
    }

    this.classicEditorReady = new Promise((resolve) => {
      const start = Date.now();
      const check = (): void => {
        if (typeof ClassicEditor !== "undefined") {
          resolve(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          this.classicEditorReady = null;
          resolve(false);
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });

    return this.classicEditorReady;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const ckEditorManager = CKEditorManager.getInstance();

// Auto-initialize on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
  requestAnimationFrame(() => {
    ckEditorManager.reinitialize();
  });
});

// Re-initialize after HTMX swaps
document.addEventListener("htmx:afterSettle", () => {
  ckEditorManager.reinitialize();
});

// Initialize all editors before form submission to ensure data is captured
document.addEventListener(
  "submit",
  async (event) => {
    const form = event.target as HTMLFormElement;
    if (form.querySelector('[data-ckeditor="true"]')) {
      // Already being re-submitted after initialization
      if (form.dataset.ckeditorSubmitting === "true") {
        delete form.dataset.ckeditorSubmitting;
        return;
      }

      // Only intercept if there are uninitialized editors
      // If all editors are already initialized, let the form submit normally
      if (!ckEditorManager.hasPendingEditors()) {
        return;
      }

      // Prevent form submission until all editors are initialized
      event.preventDefault();
      await ckEditorManager.initializeAll();
      // Re-submit the form after initialization
      form.dataset.ckeditorSubmitting = "true";
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        if (typeof form.reportValidity === "function") {
          if (form.reportValidity()) {
            form.submit();
            return;
          }
        } else if (form.checkValidity()) {
          form.submit();
          return;
        }
        delete form.dataset.ckeditorSubmitting;
      }
    }
  },
  { capture: true },
);

// Make globally available for manual initialization
declare global {
  interface Window {
    ckEditorManager: typeof ckEditorManager;
    CKEDITOR_LICENSE_KEY?: string;
  }
}
window.ckEditorManager = ckEditorManager;
